import React, { useEffect, useMemo, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Empty, Notice } from "./ui/index.jsx";
import { fetchFeatures, featuresToTable, fetchAttachments } from "../lib/arcgis.js";
import { keyCandidates } from "../lib/joinRows.js";
import { groupPhotos, subjectTally, bestCodeField, normalise } from "../lib/photos.js";
import { fmtDate } from "../lib/format.js";

/* Site photographs, filed under the school they were taken at.
 *
 * They live in a layer of their own with no field pointing at a school. What
 * they have is a name, AAS_BUS1, which is the school's code and the subject.
 * That is the whole link, and it holds: the codes are read off the schools
 * layer rather than guessed at, so a photograph named after something that is
 * not on this program is reported rather than filed somewhere wrong.
 *
 * The pictures themselves are files attached to each record, so they are
 * fetched separately, and only for the school somebody has opened. A hundred
 * and eighty three photographs is a lot to pull down for a screen nobody has
 * asked to look at.
 */

const CARD = 132;

function Thumb({ photo, onOpen }) {
  const [broken, setBroken] = useState(false);
  return (
    <button onClick={() => onOpen(photo)} title={photo.name}
      style={{
        border: `1px solid ${T.line}`, borderRadius: 4, background: T.chip,
        padding: 0, width: CARD, cursor: "pointer", overflow: "hidden",
        display: "grid", gap: 0,
      }}>
      <div style={{ height: CARD * 0.75, display: "flex", alignItems: "center",
                    justifyContent: "center", overflow: "hidden" }}>
        {broken ? (
          <span style={{ fontSize: 11, color: T.soft, padding: 8, textAlign: "center" }}>
            Could not load
          </span>
        ) : (
          <img src={photo.url} alt={photo.name} loading="lazy"
            onError={() => setBroken(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        )}
      </div>
      <div style={{ padding: "5px 7px", fontSize: 11, color: T.ink, textAlign: "left",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {photo.subject}
      </div>
    </button>
  );
}

function Lightbox({ photo, onClose }) {
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = held;
      window.removeEventListener("keydown", key);
    };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 80, background: "rgba(20,40,75,0.72)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: T.panel, borderRadius: 4, padding: 12, maxWidth: "min(1000px, 94vw)" }}>
        <img src={photo.url} alt={photo.name}
          style={{ maxWidth: "100%", maxHeight: "76vh", display: "block" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: 16, marginTop: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{photo.name}</div>
            <div style={{ fontSize: 12, color: T.soft }}>
              {photo.school}{photo.taken ? ` · ${photo.taken}` : ""}
            </div>
          </div>
          <Btn small kind="ghost" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}

export default function Photographs({
  url, layerColumns, layerRows, labelKey, read = fetchFeatures, load = fetchAttachments,
}) {
  const [state, setState] = useState({ loading: true });
  const [openCode, setOpenCode] = useState(null);
  const [files, setFiles] = useState(new Map());
  const [fetching, setFetching] = useState(false);
  const [showing, setShowing] = useState(null);

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    read(url, "1=1")
      .then((payload) => {
        if (!live) return;
        setState({ loading: false, ...featuresToTable(payload) });
      })
      .catch((e) => { if (live) setState({ loading: false, error: e.message }); });
    return () => { live = false; };
  }, [read, url]);

  const nameKey = useMemo(() => {
    const cols = state.columns ?? [];
    return (cols.find((c) => /^name$/i.test(c.key)) ?? cols.find((c) => c.type === "text"))?.key ?? "Name";
  }, [state.columns]);

  /* Which field the photographs are named after is worked out rather than
     configured: every field that could be a key is tried and the one that
     accounts for the most photographs wins. */
  const link = useMemo(() => {
    if (!state.rows || !layerRows?.length) return null;
    return bestCodeField({
      candidates: keyCandidates(layerColumns ?? [], layerRows),
      layerRows, photoRows: state.rows, nameKey,
    });
  }, [state.rows, layerColumns, layerRows, nameKey]);

  const grouped = useMemo(() => {
    if (!state.rows || !link) return null;
    return groupPhotos({
      photoRows: state.rows, nameKey,
      codes: layerRows.map((r) => r[link.column.key]),
    });
  }, [state.rows, link, layerRows, nameKey]);

  const schools = useMemo(() => {
    if (!grouped || !link) return [];
    return layerRows
      .map((row) => ({
        row,
        code: normalise(row[link.column.key]),
        name: labelKey ? String(row[labelKey] ?? "") : normalise(row[link.column.key]),
      }))
      .map((s) => ({ ...s, photos: grouped.byCode.get(s.code) ?? [] }))
      .filter((s) => s.photos.length)
      .sort((a, b) => b.photos.length - a.photos.length || a.name.localeCompare(b.name));
  }, [grouped, link, layerRows, labelKey]);

  const open = schools.find((s) => s.code === openCode) ?? null;

  /* Only the school somebody opened. The pictures are separate files and there
     is no reason to pull down every one of them for a panel nobody has
     clicked into. */
  useEffect(() => {
    if (!open || files.has(open.code)) return undefined;
    let live = true;
    setFetching(true);
    load(url, open.photos.map((p) => p.OBJECTID ?? p._id))
      .then((found) => {
        if (!live) return;
        setFiles((f) => new Map(f).set(open.code, found));
      })
      .finally(() => { if (live) setFetching(false); });
    return () => { live = false; };
  }, [open, url, load, files]);

  const vocabulary = useMemo(() => {
    if (!state.rows || !link) return [];
    return subjectTally({
      photoRows: state.rows, nameKey,
      codes: layerRows.map((r) => r[link.column.key]),
    });
  }, [state.rows, link, layerRows, nameKey]);

  if (state.loading) return <Panel title="Site photographs"><Empty text="Reading the photo layer..." /></Panel>;
  if (state.error) {
    return (
      <Panel title="Site photographs">
        <Notice kind="error">{state.error}</Notice>
      </Panel>
    );
  }

  if (!link) {
    return (
      <Panel title="Site photographs">
        <Notice kind="error">
          {state.rows?.length ?? 0} photographs came back, and none of them are
          named after anything in the layer above, so there is no way to tell
          which school each one belongs to. They are named like AAS_BUS1 on this
          program, where AAS is the school's own code.
        </Notice>
      </Panel>
    );
  }

  const shown = open ? (files.get(open.code) ?? new Map()) : new Map();
  const pictures = open
    ? open.photos.flatMap((p) => (shown.get(p.OBJECTID) ?? []).map((file) => ({
      ...file,
      subject: p._subject,
      school: open.name,
      taken: p.DateTime ? fmtDate(String(p.DateTime).slice(0, 10)) : "",
    })))
    : [];

  return (
    <Panel title="Site photographs" accent={GOLD_INK}
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Chip color={GOLD_INK}>{grouped.matched} of {state.rows.length} placed</Chip>
          <Chip color={T.steel}>{schools.length} schools</Chip>
        </div>
      }>
      {showing && <Lightbox photo={showing} onClose={() => setShowing(null)} />}

      <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12, lineHeight: 1.6 }}>
        Filed by the code in each photograph's name, matched against
        {" "}{link.column.label.toLowerCase()} in the layer above. Pick a school
        to see its pictures.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {schools.map((s) => (
          <button key={s.code} onClick={() => setOpenCode(s.code === openCode ? null : s.code)}
            style={{
              border: `1px solid ${s.code === openCode ? T.navy : T.line}`,
              background: s.code === openCode ? T.navy : T.panel,
              color: s.code === openCode ? "#fff" : T.ink,
              borderRadius: 4, padding: "5px 11px", fontSize: 12.5, cursor: "pointer",
            }}>
            {s.name} <span style={{ opacity: 0.75 }}>{s.photos.length}</span>
          </button>
        ))}
      </div>

      {open && (
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.navy, marginBottom: 8 }}>
            {open.name}
          </div>
          {fetching && !pictures.length && <Empty text="Fetching the pictures..." />}
          {!fetching && !pictures.length && (
            <Notice>
              These {open.photos.length} records carry no picture files, so there
              is nothing to show. The records themselves are listed below.
            </Notice>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {pictures.map((p) => (
              <Thumb key={`${p.id}-${p.subject}`} photo={p} onOpen={setShowing} />
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 10 }}>
            {open.photos.map((p) => p._subject).join(", ")}
          </div>
        </div>
      )}

      {grouped.unmatched.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Notice kind="error">
            {grouped.unmatched.length} photograph
            {grouped.unmatched.length === 1 ? " is" : "s are"} named after
            no school on this program, so {grouped.unmatched.length === 1 ? "it is" : "they are"}
            {" "}not filed anywhere:{" "}
            {grouped.unmatched.slice(0, 8).map((r) => String(r[nameKey])).join(", ")}
            {grouped.unmatched.length > 8 ? ` and ${grouped.unmatched.length - 8} more` : ""}.
          </Notice>
        </div>
      )}

      {vocabulary.length > 0 && (
        <div style={{ fontSize: 12, color: T.soft, marginTop: 14, lineHeight: 1.7 }}>
          <span style={{ fontWeight: 700, color: T.ink }}>What the names cover: </span>
          {vocabulary.map((v) => `${v.kind} (${v.count})`).join(", ")}. These are
          read off the photographs themselves rather than from a list, because
          nobody has confirmed what the abbreviations stand for. Once somebody
          does, they can be named properly and counted against the interface
          categories.
        </div>
      )}
    </Panel>
  );
}
